import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import LinkIcon from "../../../../assets/images/icons/link.png";

type Props = {
  isXsOrSm: boolean;
  tabIndex: number;
  userProfile: any;
  coApplicantDetails: any;
  partnerStartedJourney: boolean;
  documentApplicantType: string;
  onTriggerLinkToCollectDocuments: () => void;
  mainApplicantDetails:any;
  fetchUserApplicationData: any;
  applicants: string[];
  hideHeaderActions: boolean;
  documentsReadOnly?: boolean;
};

export function ApplicantInfoBox({
  isXsOrSm,
  tabIndex,
  userProfile,
  coApplicantDetails,
  partnerStartedJourney,
  documentApplicantType,
  onTriggerLinkToCollectDocuments,
  mainApplicantDetails,
  fetchUserApplicationData,
  applicants,
  hideHeaderActions,
  documentsReadOnly = false,
}: Props) {
  const coApplicantIndexFromDocType = useMemo(() => {
    const idx = Number(documentApplicantType?.split("Co-Applicant ")?.[1]);
    return Number.isFinite(idx) ? idx : 0;
  }, [documentApplicantType]);
  const applicantName = useMemo(() => {
    if (tabIndex === 0) {
      // If partner started journey, use mainApplicantDetails from handleFetchPartnerLeadDetailsApi
      if (partnerStartedJourney && !hideHeaderActions) {
        const result = (mainApplicantDetails || []).reduce((acc:any, item:any) => {
          if (["firstName", "lastName"].includes(item.name)) {
            acc[item.name] = item.value;
          }
          return acc;
        }, {});

        if (result && result?.firstName && result?.lastName){
          return `${result?.firstName} ${result?.lastName} (Primary Applicant)`;
        }
        return "(Primary Applicant)";
      }
      
      // If partner started journey is false, extract from fetchUserApplicationData -> Eligibility Check -> verticalSteps
      if (fetchUserApplicationData?.horizontalSteps?.[0]?.verticalSteps) {
        const eligibilityCheckVerticalSteps = fetchUserApplicationData.horizontalSteps[0].verticalSteps;
        let firstName = "";
        let lastName = "";
        
        // Iterate through all verticalSteps to find firstName and lastName fields
        for (const verticalStep of eligibilityCheckVerticalSteps) {
          if (verticalStep?.fields && Array.isArray(verticalStep.fields)) {
            for (const field of verticalStep.fields) {
              if (field?.firstName) {
                firstName = field.firstName;
              }

              if (field?.lastName) {
                lastName = field.lastName;
              }
            }
          }
        }
        
        if (firstName && lastName) {
          return `${firstName} ${lastName} (Primary Applicant)`;
        }
      }
      
      return "(Primary Applicant)";
    }

    if (partnerStartedJourney && !hideHeaderActions) {
      return (
        coApplicantDetails?.[`coapplicant${applicants?.[tabIndex]?.split(" ")?.[1]}` as keyof typeof coApplicantDetails]?.coApplicantName ?? ""
      );
    }

    const first = coApplicantDetails?.[coApplicantIndexFromDocType]?.FirstName ?? "";
    const last = coApplicantDetails?.[coApplicantIndexFromDocType]?.LastName ?? "";
    return `${first} ${last}`.trim();
  }, [applicants, tabIndex, partnerStartedJourney, coApplicantDetails, coApplicantIndexFromDocType, mainApplicantDetails, fetchUserApplicationData]);

  const applicantContact = useMemo(() => {
    if (tabIndex === 0) {
      // If partner started journey, use mainApplicantDetails from handleFetchPartnerLeadDetailsApi
      if (partnerStartedJourney && !hideHeaderActions) {
        const result = (mainApplicantDetails || []).reduce((acc:any, item:any) => {
          if (["email", "phone_number"].includes(item.name)) {
            acc[item.name] = item.value;
          }
          return acc;
        }, {});

        const email = result?.email;
        const phone_number = result?.phone_number;
        email && phone_number ? `Email: ${email} | Phone: ${phone_number}` : `Email:  NA | Phone: NA`;
      }
      
      // If partner started journey is false, extract from fetchUserApplicationData -> Eligibility Check -> verticalSteps
      let email = "NA";
      let phone_number = "NA";
      if (fetchUserApplicationData?.horizontalSteps?.[0]?.verticalSteps) {
        const eligibilityCheckVerticalSteps = fetchUserApplicationData.horizontalSteps[0].verticalSteps;
        
        // Iterate through all verticalSteps to find email and phone_number fields
        for (const verticalStep of eligibilityCheckVerticalSteps) {
          if (verticalStep?.fields && Array.isArray(verticalStep.fields)) {
            for (const field of verticalStep.fields) {
              if (field?.email) {
                email = field.email;
              }
              if (field?.phone_number) {
                phone_number = field.phone_number;
              }
            }
          }
        }
      }
      
      return email && phone_number ? `Email: ${email} | Phone: ${phone_number}` : `Email:  NA | Phone: NA`;
    }

    if (partnerStartedJourney && !hideHeaderActions) {
      const email = coApplicantDetails?.[`coapplicant${applicants?.[tabIndex]?.split(" ")?.[1]}` as keyof typeof coApplicantDetails]?.coApplicantEmail ?? "NA";
      const phone = coApplicantDetails?.[`coapplicant${applicants?.[tabIndex]?.split(" ")?.[1]}` as keyof typeof coApplicantDetails]?.coApplicantPhone ?? "NA";
      return email && phone ? `Email:  ${email} | Phone: ${phone}` : `Email:  NA | Phone: NA`;
    }

    const email = coApplicantDetails?.[coApplicantIndexFromDocType]?.Email;
    const phone = coApplicantDetails?.[coApplicantIndexFromDocType]?.PhoneNumber;
     return email && phone ? `Email:  ${email} | Phone: ${phone}` : `Email:  NA | Phone: NA`;
  }, [applicants, tabIndex, partnerStartedJourney, coApplicantDetails, coApplicantIndexFromDocType, mainApplicantDetails, fetchUserApplicationData]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        bgcolor: "#fff3cf",
        borderRadius: "8px",
        flexWrap: "wrap",
        gap: 1,
        p: 2,
        mb: 2,
      }}
    >
      {isXsOrSm ? (
        <Typography sx={{ fontSize: "13px", color: "#5d586c" }}>
          <span style={{ fontWeight: 600, fontSize: "15px", color: "#1F2233" }}>
            {applicantName}
          </span>
          <br />
          <span>{applicantContact}</span>
        </Typography>
      ) : (
        <Typography sx={{ flexShrink: 0, fontSize: "13px", color: "#5d586c" }}>
          <span
            style={{
              fontWeight: 600,
              fontSize: "15px",
              color: "#1F2233",
              marginRight: "8px",
            }}
          >
            {applicantName}
          </span>{" "}
          {applicantContact}
        </Typography>
      )}

      {tabIndex > 0 && !partnerStartedJourney && !documentsReadOnly && (
        <Typography
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: coApplicantDetails?.[coApplicantIndexFromDocType]?.Email && coApplicantDetails?.[coApplicantIndexFromDocType]?.PhoneNumber ? "#00b3b5" : "gray",
            cursor: coApplicantDetails?.[coApplicantIndexFromDocType]?.Email && coApplicantDetails?.[coApplicantIndexFromDocType]?.PhoneNumber ? "pointer" : "not-allowed",
            fontSize: "0.85rem",
          }}
          onClick={() => {
             if(coApplicantDetails?.[coApplicantIndexFromDocType]?.Email && coApplicantDetails?.[coApplicantIndexFromDocType]?.PhoneNumber) {
              onTriggerLinkToCollectDocuments();
             }}
            }
        >
          <img src={LinkIcon} alt="link" style={{ height: 16, marginRight: 4 }} />{" "}
          Trigger Link to Collect documents
        </Typography>
      )}
    </Box>
  );
}
